
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import request from '@/utils/request'

interface CaneBonusEntry {
  deviceType: string
  bonusSeconds: number
}

interface MonthlyCreditsConfig {
  id: number
  countryCode: string
  countryName: string | null
  creditsSeconds: number
  isActive: boolean
  priority: number
  configType: string
  caneOwnerBonus: number
  caneBonuses: Array<{ id: number; deviceType: string; bonusSeconds: number }>
  createdAt: string
  updatedAt: string
}

interface JobExecutionRecord {
  id: number
  jobName: string
  status: string
  startedAt: string
  completedAt: string
  durationMs: number
  totalUsers: number
  successCount: number
  errorCount: number
  skippedCount: number
  resultJson: any
  errorMessage: string | null
}

interface MonthlyStat {
  month_key: string
  total_grants: number
  unique_users: number
}

@Component({
  name: 'MonthlyCreditsConfig'
})
export default class extends Vue {
  private activeTab = 'monthly'
  private list: MonthlyCreditsConfig[] = []
  private listLoading = false
  private listQuery = {
    isActive: null as boolean | null
  }

  private jobExecutions: JobExecutionRecord[] = []
  private lastJobExecution: JobExecutionRecord | null = null
  private monthlyStats: MonthlyStat[] = []

  private dialogFormVisible = false
  private dialogStatus = 'create'
  private submitLoading = false

  private deviceTypes = ['V1', 'V2']

  private formData = {
    id: null as number | null,
    countryCode: '',
    countryName: '',
    creditsSeconds: 300,
    isActive: true,
    priority: 10,
    caneOwnerBonus: 0,
    caneBonuses: [] as CaneBonusEntry[]
  }

  private rules = {
    countryCode: [
      { required: true, message: 'Country code is required', trigger: 'blur' },
      { type: 'string', min: 2, max: 10, message: 'Country code must be 2-10 characters', trigger: 'blur' }
    ],
    creditsSeconds: [
      { required: true, message: 'Credits (seconds) is required', trigger: 'blur' }
    ]
  }

  created() {
    this.getList()
    this.getJobHistory()
    this.getMonthlyStats()
  }

  private handleTabChange() {
    this.listQuery.isActive = null
    this.getList()
  }

  private async getList() {
    this.listLoading = true
    try {
      const params: any = {
        configType: this.activeTab
      }
      if (this.listQuery.isActive !== null) {
        params.isActive = this.listQuery.isActive
      }

      const response = await request.get('/admin/monthly-credits-config', { params })
      if (response.data.status.code === 'success') {
        this.list = response.data.data
      }
    } catch (error) {
      this.$message.error('Failed to load configurations')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private async getJobHistory() {
    try {
      const response = await request.get('/admin/monthly-credits-config/job-history')
      if (response.data.status.code === 'success') {
        this.jobExecutions = response.data.data
        if (this.jobExecutions.length > 0) {
          this.lastJobExecution = this.jobExecutions[0]
        }
      }
    } catch (error) {
      console.error('Failed to load job history', error)
    }
  }

  private async getMonthlyStats() {
    try {
      const response = await request.get('/admin/monthly-credits-config/stats')
      if (response.data.status.code === 'success') {
        this.monthlyStats = response.data.data
      }
    } catch (error) {
      console.error('Failed to load monthly stats', error)
    }
  }

  private resetForm() {
    this.formData = {
      id: null,
      countryCode: '',
      countryName: '',
      creditsSeconds: 300,
      isActive: true,
      priority: 10,
      caneOwnerBonus: 0,
      caneBonuses: []
    }
  }

  private handleCreate() {
    this.resetForm()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as ElForm)?.clearValidate()
    })
  }

  private handleEdit(row: MonthlyCreditsConfig) {
    this.formData = {
      id: row.id,
      countryCode: row.countryCode,
      countryName: row.countryName || '',
      creditsSeconds: row.creditsSeconds,
      isActive: row.isActive,
      priority: row.priority,
      caneOwnerBonus: row.caneOwnerBonus || 0,
      caneBonuses: (row.caneBonuses || []).map(cb => ({
        deviceType: cb.deviceType,
        bonusSeconds: cb.bonusSeconds
      }))
    }
    this.dialogStatus = 'update'
    this.dialogFormVisible = true
  }

  private setCredits(seconds: number) {
    this.formData.creditsSeconds = seconds
  }

  private addCaneBonus() {
    const available = this.availableDeviceTypes(-1)
    if (available.length === 0) return
    this.formData.caneBonuses.push({
      deviceType: available[0],
      bonusSeconds: 0
    })
  }

  private removeCaneBonus(index: number) {
    this.formData.caneBonuses.splice(index, 1)
  }

  private availableDeviceTypes(currentIndex: number): string[] {
    const usedTypes = this.formData.caneBonuses
      .filter((_: CaneBonusEntry, i: number) => i !== currentIndex)
      .map((b: CaneBonusEntry) => b.deviceType)
    return this.deviceTypes.filter(dt => !usedTypes.includes(dt))
  }

  private async submitForm() {
    (this.$refs.dataForm as ElForm).validate(async (valid: boolean) => {
      if (!valid) return

      this.submitLoading = true
      try {
        const data: any = {
          countryCode: this.formData.countryCode.toUpperCase().trim(),
          countryName: this.formData.countryName || null,
          creditsSeconds: this.formData.creditsSeconds,
          isActive: this.formData.isActive,
          priority: this.formData.priority
        }

        if (this.activeTab === 'monthly') {
          data.caneOwnerBonus = this.formData.caneOwnerBonus
          data.caneBonuses = this.formData.caneBonuses.filter(
            (b: CaneBonusEntry) => b.deviceType && b.bonusSeconds >= 0
          )
        }

        if (this.dialogStatus === 'create') {
          data.configType = this.activeTab
          await request.post('/admin/monthly-credits-config', data)
          this.$message.success('Configuration created successfully')
        } else {
          await request.put(`/admin/monthly-credits-config/${this.formData.id}`, data)
          this.$message.success('Configuration updated successfully')
        }

        this.dialogFormVisible = false
        this.getList()
      } catch (error: any) {
        const errorMessage = error.response?.data?.status?.message || 'Operation failed'
        this.$message.error(errorMessage)
        console.error(error)
      } finally {
        this.submitLoading = false
      }
    })
  }

  private async handleStatusChange(row: MonthlyCreditsConfig) {
    try {
      await request.put(`/admin/monthly-credits-config/${row.id}`, {
        isActive: row.isActive
      })
      this.$message.success('Status updated')
    } catch (error) {
      row.isActive = !row.isActive
      this.$message.error('Failed to update status')
    }
  }

  private async handleDelete(row: MonthlyCreditsConfig) {
    if (row.countryCode === 'DEFAULT') {
      this.$message.warning('Cannot delete DEFAULT configuration')
      return
    }

    try {
      await this.$confirm(
        `Are you sure you want to delete the configuration for "${row.countryCode}"?`,
        'Warning',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )

      await request.delete(`/admin/monthly-credits-config/${row.id}`)
      this.$message.success('Configuration deleted successfully')
      this.getList()
    } catch (error: any) {
      if (error !== 'cancel') {
        this.$message.error('Failed to delete configuration')
        console.error(error)
      }
    }
  }

  private formatCreditsByCountry(creditsByCountry: Record<string, { count: number; seconds: number }>) {
    return Object.entries(creditsByCountry).map(([country, data]) => ({
      country,
      count: data.count,
      seconds: data.seconds,
      minutes: this.formatMinutes(data.seconds)
    }))
  }

  private formatMonthKey(monthKey: string): string {
    if (!monthKey) return '-'
    const parts = monthKey.split('-')
    if (parts.length !== 2) return monthKey
    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const monthNum = parseInt(parts[1], 10)
    return `${monthNames[monthNum] || parts[1]} ${parts[0]}`
  }

  private formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '-'
    return num.toLocaleString('en-US')
  }

  private formatDuration(ms: number | null | undefined): string {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSec = seconds % 60
    return `${minutes}m ${remainingSec}s`
  }

  private formatMinutes(seconds: number): string {
    if (!seconds || seconds === 0) return '0 minutes'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    return `${minutes}m ${remainingSeconds}s`
  }

  private formatDate(date: string | undefined): string {
    if (!date) return '-'
    const d = new Date(date)
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  }
}
